import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";

// Custom components for PortableText rendering
const portableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) return null;
      const imageUrl = `https://cdn.sanity.io/images/wtagmpvu/production/${value.asset._ref
        .replace("image-", "")
        .replace("-jpg", ".jpg")
        .replace("-png", ".png")
        .replace("-webp", ".webp")
        .replace("-jpeg", ".jpeg")}`;
      return (
        <img
          src={imageUrl}
          alt={value.alt || ""}
          className="w-full rounded-lg my-6"
        />
      );
    },
  },
  block: {
    h2: ({ children }) => (
      <h2 className="text-2xl font-bold mt-8 mb-4 text-gray-900">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl font-bold mt-6 mb-3 text-gray-900">{children}</h3>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary pl-4 my-4 italic text-gray-700">
        {children}
      </blockquote>
    ),
    normal: ({ children }) => (
      <p className="text-gray-700 leading-relaxed mb-4">{children}</p>
    ),
  },
  marks: {
    link: ({ children, value }) => (
      <a
        href={value.href}
        className="text-theme hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const query = `*[_type == "blog" && slug.current == "${slug}"][0] {
      _id,
      title,
      slug,
      publishedAt,
      metaTitle,
      metaDescription,
      body,
      "featuredImageUrl": featuredImage.asset->url
    }`;
    const encodedQuery = encodeURIComponent(query);

    fetch(
      `https://wtagmpvu.apicdn.sanity.io/v2025-10-24/data/query/production?query=${encodedQuery}&perspective=published`,
    )
      .then((response) => response.json())
      .then((data) => {
        setPost(data.result);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching blog post:", error);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900">Post not found</h1>
        <Link
          to="/blog"
          className="text-black hover:text-black hover:underline"
        >
          ← Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.metaTitle || post.title}</title>
        <meta name="description" content={post.metaDescription || ""} />
        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={post.metaDescription || ""} />
        {post.featuredImageUrl && (
          <meta property="og:image" content={post.featuredImageUrl} />
        )}
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <article className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/blog"
            className="text-black hover:text-black hover:underline mb-6 inline-block"
          >
            ← Back to Blog
          </Link>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            {post.title}
          </h1>

          {/* Date */}
          <p className="text-gray-500 mb-6">
            {dayjs(post.publishedAt).format("D MMMM YYYY")}
          </p>

          {/* Featured Image */}
          {post.featuredImageUrl && (
            <img
              src={post.featuredImageUrl}
              alt={post.title}
              className="w-full rounded-lg mb-8"
            />
          )}

          {/* Body - using official PortableText component */}
          {post.body && (
            <div className="prose prose-lg max-w-none">
              <PortableText
                value={post.body}
                components={portableTextComponents}
              />
            </div>
          )}
        </article>
      </div>
    </>
  );
}
